import React, { useCallback, useEffect, useState } from 'react'

import { z } from 'zod'

import IconClose from '@/assets/icons/icon-close.svg'
import { classNames } from '@/utils/helpers'

export type ErrorMatchers = Record<string, ErrorMessageMatch | 'default'>

const defaultMatchers: ErrorMatchers = {
  'The requested resource could not be found.': { status: 404 },
  'You are not permitted to perform this action.': { status: 403 },
  'You are not authenticated.': { status: 401 },
  'The request failed.': { status: 408 },
  'This resource already exists': { status: 409 },
  'There has been an error while processing your request.': { status: 500 },
  'The request timed out.': { status: 504 },
}

export const matchers: Record<string, ErrorMessageMatch> = {
  conflict: { status: 409 },
} as const

export function extendDefaultMatchers(matchers: ErrorMatchers): ErrorMatchers {
  const reducedDefaults = { ...defaultMatchers }

  for (const [, matcher] of Object.entries(matchers)) {
    if (matcher === 'default') {
      continue
    }
    if (typeof matcher.status === 'number') {
      for (const [key, currentMatcher] of Object.entries(reducedDefaults)) {
        if (
          currentMatcher != 'default' &&
          currentMatcher.status === matcher.status
        ) {
          delete reducedDefaults[key]
        }
      }
    }
  }

  return { ...reducedDefaults, ...matchers }
}

export interface ErrorMessageMatch {
  status?: number | number[]
  messageContains?:
    | string
    | { allOf: string[] }
    | { anyOf: string[] }
    | { noneOf: string[] }
  matches?: (error: z.infer<typeof ErrorSchema>) => boolean
  priority?: number
}

const ErrorSchema = z.object({
  response: z
    .object({
      status: z.number().optional(),
    })
    .optional(),
  message: z.string().optional(),
})

function matches(
  matcher: ErrorMessageMatch,
  error: z.infer<typeof ErrorSchema>,
) {
  if (error.response?.status && matcher.status) {
    return Array.isArray(matcher.status)
      ? matcher.status.includes(error.response.status)
      : matcher.status === (error.response.status as number)
  }
  if (error.message && matcher.messageContains) {
    const message = error.message

    if (typeof matcher.messageContains === 'string') {
      return error.message.includes(matcher.messageContains)
    } else if ('allOf' in matcher.messageContains) {
      return matcher.messageContains.allOf.every((it) => message.includes(it))
    } else if ('anyOf' in matcher.messageContains) {
      return matcher.messageContains.anyOf.every((it) => message.includes(it))
    } else if ('noneOf' in matcher.messageContains) {
      return matcher.messageContains.noneOf.every((it) => message.includes(it))
    }
  }

  return matcher.matches ? matcher.matches(error) : false
}

export function useErrorMessage(matchers: ErrorMatchers) {
  return useCallback(
    (unparsedError: unknown) => {
      const error = ErrorSchema.parse(unparsedError)

      for (const [message, matcher] of Object.entries(matchers)) {
        if (matcher === 'default') {
          continue
        }
        if (matches(matcher as ErrorMessageMatch, error)) {
          return message
        }
      }
      for (const [message, matcher] of Object.entries(matchers)) {
        if (matcher === 'default') {
          return message
        }
      }

      return 'Unknown error'
    },
    [matchers],
  )
}

interface ErrorProps {
  error: unknown
  light?: boolean
  matchers?: Record<string, ErrorMessageMatch | 'default'>
  closable?: boolean
  testId?: string
}

export function useErrorReset(error: unknown) {
  const [value, setValue] = useState<unknown | null>(error)
  const reset = useCallback(() => setValue(null), [])

  useEffect(() => setValue(error), [error])

  return [value, reset] as const
}

export const Error = ({
  error,
  matchers,
  className,
  closable,
  testId,
  ...props
}: React.ComponentProps<'span'> & ErrorProps) => {
  const [closed, setClosed] = useState(false)
  const handleClose = useCallback(() => setClosed(true), [])

  useEffect(() => {
    setClosed(false)
  }, [error])

  if (closed) {
    return null
  }
  if (!error) {
    return null
  }

  return (
    <span
      {...props}
      className={classNames(
        'relative block px-3 py-2 text-xs rounded-lg font-normal bg-danger-light text-danger-default',
        className,
      )}
    >
      <span
        className="truncate line-clamp-3"
        data-testid={testId}
      >
        <ErrorMessage
          error={error}
          matchers={matchers}
        />
      </span>
      {closable && (
        <span className="absolute inset-0 flex items-center justify-end px-3">
          <button
            onClick={handleClose}
            className="cursor-pointer p-1 -mx-1 rounded-lg transition duration-200 ease-out hover:bg-danger-default/10"
          >
            <IconClose className="w-3 h-3" />
          </button>
        </span>
      )}
    </span>
  )
}

export const ErrorMessage = ({ error, matchers }: ErrorProps) => {
  const formatMessage = useErrorMessage(matchers ?? defaultMatchers)

  return error ? formatMessage(error) : null
}
